import {
	Box,
	InputAdornment,
	OutlinedInput,
	outlinedInputClasses,
} from '@mui/material'
import React from 'react'
import { COLORS } from 'styles/colors'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

type RangeBound = number | string | null | undefined

interface RangeInputsProps {
	startValue: RangeBound
	endValue: RangeBound
	availableStart?: RangeBound
	availableEnd?: RangeBound
	fallbackPlaceholder?: string
	disabled?: boolean
	formatValue?: (value: number | string) => number | string
	onStartChange: (event: React.ChangeEvent<HTMLInputElement>) => void
	onEndChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const isEmptyBound = (value: RangeBound) => value === '' || value == null

const RangeInputs = ({
	startValue,
	endValue,
	availableStart,
	availableEnd,
	fallbackPlaceholder = 'Дни',
	disabled = false,
	formatValue = value => value,
	onStartChange,
	onEndChange,
}: RangeInputsProps) => {
	const fontColor = disabled ? COLORS.secondaryFont : COLORS.black
	const hasError =
		!isEmptyBound(startValue) &&
		!isEmptyBound(endValue) &&
		Number(startValue) > Number(endValue)

	const inputSx = {
		width: '120px',
		height: '54px',
		borderRadius: '8px',
		justifyContent: 'center',
		background: 'white',
		'& .MuiOutlinedInput-notchedOutline': {
			borderRadius: '8px',
			borderColor: COLORS.inputBorder,
		},
		'& .MuiInputAdornment-root': {
			marginRight: '4px',
		},
		'& input': {
			textAlign: 'left',
			flex: '0 1 auto',
			width: '48px',
			paddingLeft: 0,
			fontSize: '14px',
			color: fontColor,
			WebkitTextFillColor: fontColor,
		},
		'& input::placeholder': {
			color: COLORS.secondaryFont,
			opacity: 1,
		},
		[`&:hover .${outlinedInputClasses.notchedOutline}`]: {
			borderColor: disabled ? COLORS.inputBorder : COLORS.main,
		},
		[`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
			border: `1px solid ${COLORS.main}`,
		},
		[`&.Mui-error .${outlinedInputClasses.notchedOutline}`]: {
			borderColor: COLORS.error,
		},
		'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
			borderColor: `${COLORS.inputBorder} !important`,
		},
	}

	const renderInput = (
		label: string,
		value: RangeBound,
		available: RangeBound,
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
	) => (
		<OutlinedInput
			label='none'
			notched={false}
			type='text'
			inputMode='decimal'
			disabled={disabled}
			error={hasError}
			placeholder={
				isEmptyBound(available) ? fallbackPlaceholder : `${available}`
			}
			value={isEmptyBound(value) ? '' : formatValue(value as number | string)}
			startAdornment={
				<InputAdornment position='start'>
					<TypographySecondary sx={{ color: COLORS.secondaryFont }}>
						{label}
					</TypographySecondary>
				</InputAdornment>
			}
			onChange={onChange}
			sx={inputSx}
		/>
	)

	return (
		<Box display='flex' flexDirection='row' alignItems='center' gap='8px'>
			{renderInput('от', startValue, availableStart, onStartChange)}
			<TypographySecondary>-</TypographySecondary>
			{renderInput('до', endValue, availableEnd, onEndChange)}
		</Box>
	)
}

export default RangeInputs
